/**
 * 个人页面 (My Page) - 复用Home的AppShell框架
 * 显示用户的个人信息、我的视频、浏览历史与我的合集
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getToken, saveAuth, getUser, isAuthenticated, type User } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import { UserInfoCard, MyStickyTabs, type MyPageTab } from './MyStickyHeader';
import { MyVideoList } from './MyVideoList';
import { MyCollectionList } from './MyCollectionList';
import './MyPage.css';
import './common/ListStatus.css';
import ListStatusFooter from './common/ListStatusFooter';
import { signInStateProfile } from '../utils/signInReturn';

const MyPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => Boolean(getToken()));
  const location = useLocation();

  const getTabFromSearch = (): MyPageTab => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'history') return 'history';
    if (tab === 'collections') return 'collections';
    return 'management';
  };

  const [activeTab, setActiveTab] = useState<MyPageTab>(() => getTabFromSearch());
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [delayedLoading, setDelayedLoading] = useState(false);

  const navigate = useNavigate();
  const scrollElRef = useRef<HTMLElement | null>(null);
  const myVideoListRef = useRef<any>(null);

  // 初始化滚动容器引用
  useEffect(() => {
    scrollElRef.current = document.querySelector('.app-shell-body') as HTMLElement | null;
  }, []);

  // 处理tab切换由 MyStickyHeader 触发硬刷新（通过 URL query），这里仅用于保持 hasMore when navigating via history
  const handleTabChange = (tab: MyPageTab) => {
    // noop for hard-reload behavior; kept for compatibility if needed
    setHasMore(true);
  };

  // 如果地址栏的 query 改变（后退/前进等），同步 activeTab 到 state
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    const next: MyPageTab =
      tab === 'history' ? 'history' : tab === 'collections' ? 'collections' : 'management';
    setActiveTab(next);
  }, [location.search]);

  // 延迟显示 loading（避免瞬间切换闪烁）
  useEffect(() => {
    let timer: number | null = null;
    if (listLoading) {
      timer = window.setTimeout(() => setDelayedLoading(true), 350);
    } else {
      setDelayedLoading(false);
    }
    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [listLoading]);

  useEffect(() => {
    let didCancel = false;
    (async () => {
      if (!getToken()) {
        if (!didCancel) {
          setLoading(false);
        }
        return;
      }

      try {
        const currentUser = getUser();
        if (currentUser) {
          setUser(currentUser);
        }

        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          clearAuth();
          navigate('/sign-in', { state: signInStateProfile() });
          return;
        }

        if (!response.ok) {
          throw new Error('获取用户信息失败');
        }

        const data: ApiResponse<User> = await response.json();
        if (data.success && data.data) {
          if (!didCancel) {
            setUser(data.data);
            const token = getToken();
            if (token) {
              saveAuth({ token, user: data.data });
            }
          }
        }
      } catch (err) {
        // No AbortError expected since we don't attach a signal; log other errors.
        console.error('加载用户信息失败:', err);
      } finally {
        if (!didCancel) {
          setLoading(false);
        }
      }
    })();
    return () => {
      didCancel = true;
    };
  }, []);

  if (!isAuthenticated()) {
    return <MyPageGuestLogin />;
  }

  if (loading) {
    return (
      <div className="loading">加载中...</div>
    );
  }

  return (
    <>
      {/* User Info Card - Scrollable */}
      <div className="my-user-info-section">
        <UserInfoCard user={user} />
      </div>

      {/* Sticky Tabs - Always visible when scrolling */}
      <div className="my-sticky-tabs-container">
        <MyStickyTabs activeTab={activeTab} />
      </div>

      {/* Main Content - 复用Home的video-grid结构 */}
      <div className="video-grid">
        {activeTab === 'collections' ? (
          <MyCollectionList
            user={user}
            onEmptyStateChange={setIsEmpty}
            onListLoadingChange={setListLoading}
          />
        ) : (
          <MyVideoList
            ref={myVideoListRef}
            activeTab={activeTab}
            user={user}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onHasMoreChange={setHasMore}
            onLoadingMoreChange={setLoadingMore}
            scrollElRef={scrollElRef}
            onEmptyStateChange={setIsEmpty}
            onListLoadingChange={setListLoading}
          />
        )}
      </div>

      {/* 空/加载状态提示 - 在video-grid外面，居中显示 */}
      {delayedLoading ? (
        <div className="empty-state">
          <p>加载中...</p>
        </div>
      ) : isEmpty ? (
        <div className="empty-state">
          <p>
            {activeTab === 'management'
              ? '暂无视频'
              : activeTab === 'history'
                ? '暂无浏览历史'
                : '还没有创建任何合集'}
          </p>
        </div>
      ) : null}

      {/* 加载状态 - 仅视频/历史 tab 使用底部分页指示器；合集 tab 一次性加载，不需要 */}
      {activeTab !== 'collections' && (
        <ListStatusFooter
          loadingMore={loadingMore}
          hasMore={hasMore}
          allowNoMore={(!loading && !isEmpty && !delayedLoading)}
        />
      )}
    </>
  );
};

export default MyPage;

function MyPageGuestLogin() {
  const navigate = useNavigate();
  return (
    <div className="my-page-guest-wrap">
      <div className="my-page-guest-card">
        <div className="my-page-guest__hero" aria-hidden="true">
          <div className="my-page-guest__avatar">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
        </div>

        <h1 className="my-page-guest__title">登录后使用「我的」</h1>
        <p className="my-page-guest__subtitle">同步你的学习进度，集中管理内容与偏好设置</p>

        <ul className="my-page-guest__features">
          <li>
            <span className="my-page-guest__feature-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </span>
            <span className="my-page-guest__feature-text">我的视频、上传与编辑</span>
          </li>
          <li>
            <span className="my-page-guest__feature-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </span>
            <span className="my-page-guest__feature-text">合集整理与排序</span>
          </li>
          <li>
            <span className="my-page-guest__feature-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </span>
            <span className="my-page-guest__feature-text">浏览历史与续看</span>
          </li>
        </ul>

        <div className="my-page-guest__actions">
          <button type="button" className="my-page-guest__login" onClick={() => navigate('/sign-in', { state: signInStateProfile() })}>
            登录
          </button>
        </div>
      </div>
    </div>
  );
}
